import * as React from "react";

import {
  RobotoMarkdownLink,
  type RobotoMarkdownLinkProps,
} from "@/shared/components/RobotoMarkdown/RobotoMarkdownLink";
import { Timestamp } from "@/shared/components/RobotoMarkdown/Timestamp";

import {
  isAIInlineDurationHighlightedLink,
  isAIInlineDurationLink,
  isAIInlineTimestampHighlightedLink,
  isAIInlineTimestampLink,
  parseAIInlineDurationNs,
  parseAIInlineTimestampNs,
} from "./aiInlineTimestampLinks";
import { InlineDuration } from "./InlineDuration";
import { parseTimeFormatSettingLink } from "./parseTimeFormatSettingLink";
import { TimeFormatSettingLink } from "./TimeFormatSettingLink";

/**
 * Link renderer for AI thread markdown. Handles the thread-only `ai-ts:` /
 * `ai-ts-hl:` timestamps, the `ai-dur:` / `ai-dur-hl:` durations, and the
 * AI-authored `roboto://ui/time-format` preference switch; everything else
 * defers to {@link RobotoMarkdownLink}.
 *
 * Both an `ai-ts:` mention and a `timestamp:` link render as {@link Timestamp},
 * which carries the "Set as t=0" action.
 * Entity and topic links deliberately don't offer it, even when they carry a
 * time.
 */
export const AgentThreadMarkdownLink: React.FC<
  React.PropsWithChildren<RobotoMarkdownLinkProps>
> = ({ href, children, ...rest }) => {
  const settingFormat =
    href === undefined ? undefined : parseTimeFormatSettingLink(href);
  if (settingFormat !== undefined) {
    return (
      <TimeFormatSettingLink format={settingFormat}>
        {children}
      </TimeFormatSettingLink>
    );
  }

  if (href !== undefined && isAIInlineTimestampLink(href)) {
    const ns = parseAIInlineTimestampNs(href);
    if (ns !== undefined) {
      return (
        <Timestamp
          ns={ns}
          highlighted={isAIInlineTimestampHighlightedLink(href)}
        />
      );
    }
    // Fail closed: an `ai-ts:` / `ai-ts-hl:` link with an unparseable
    // payload signals an upstream plugin defect. Render the original
    // label as plain text rather than emitting a broken `<a>`, so the
    // bug doesn't hide behind innocuous-looking output.
    return <>{children}</>;
  }

  if (href !== undefined && isAIInlineDurationLink(href)) {
    const ns = parseAIInlineDurationNs(href);
    if (ns !== undefined) {
      return (
        <InlineDuration
          ns={ns}
          highlighted={isAIInlineDurationHighlightedLink(href)}
        />
      );
    }
    return <>{children}</>;
  }

  return (
    <RobotoMarkdownLink href={href} {...rest}>
      {children}
    </RobotoMarkdownLink>
  );
};
